
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { format } from 'date-fns';
import { DownloadCloud, Search, Filter, BarChart2, DollarSign, Users } from 'lucide-react';
import { useCampaigns } from '@/hooks';

const CampaignReportsSection: React.FC = () => {
  const { campaignReports } = useCampaigns();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [reportsTab, setReportsTab] = useState('campaigns');

  // Function to filter campaign reports based on search query and status filter
  const getFilteredReports = () => {
    return campaignReports.filter(report => {
      const matchesSearch = report.campaignName.toLowerCase().includes(searchQuery.toLowerCase()) || 
                            report.globalCampaignId.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesStatus = statusFilter === 'all' || report.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  };

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'paused':
        return 'bg-amber-100 text-amber-800';
      case 'completed':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const handleExportCSV = () => {
    const reports = getFilteredReports();
    if (reports.length === 0) return;

    const headers = ['Global ID', 'Campaign Name', 'Start Date', 'Status', 'Total Referrals', 'Successful Referrals', 'Conversion Rate', 'Total Rewards'];
    
    const csvData = reports.map(report => [
      report.globalCampaignId,
      report.campaignName,
      format(new Date(report.startDate), 'yyyy-MM-dd'),
      report.status,
      report.totalReferrals,
      report.successfulReferrals,
      `${(report.conversionRate * 100).toFixed(1)}%`,
      `$${report.totalRewards.toFixed(2)}`
    ]);
    
    // Prepend headers to data
    csvData.unshift(headers);
    
    // Convert to CSV format
    const csvContent = csvData.map(row => row.join(',')).join('\n');
    
    // Create a Blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `campaign-reports-${format(new Date(), 'yyyy-MM-dd')}.csv`);
    link.click();
  };

  // Mock data for affiliate reports
  const mockAffiliateReports = [
    {
      id: "aff-report-1",
      period: "April 2025",
      affiliates: 24,
      sales: 132,
      commissions: 1582.50,
      conversionRate: 8.2,
      generated: "2025-04-15"
    },
    {
      id: "aff-report-2",
      period: "March 2025",
      affiliates: 18,
      sales: 98,
      commissions: 1176.00,
      conversionRate: 7.5,
      generated: "2025-03-31"
    },
    {
      id: "aff-report-3",
      period: "February 2025",
      affiliates: 15,
      sales: 72,
      commissions: 864.00,
      conversionRate: 6.8,
      generated: "2025-02-28"
    }
  ];

  const handleExportAffiliateCSV = () => {
    const headers = ['Period', 'Active Affiliates', 'Total Sales', 'Total Commissions', 'Conversion Rate'];
    
    const csvData = mockAffiliateReports.map(report => [
      report.period,
      report.affiliates.toString(),
      report.sales.toString(),
      `$${report.commissions.toFixed(2)}`,
      `${report.conversionRate.toFixed(1)}%`
    ]);
    
    // Prepend headers to data
    csvData.unshift(headers);
    
    // Convert to CSV format
    const csvContent = csvData.map(row => row.join(',')).join('\n');
    
    // Create a Blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `affiliate-reports-${format(new Date(), 'yyyy-MM-dd')}.csv`);
    link.click();
  };

  return (
    <div className="space-y-6">
      <Tabs value={reportsTab} onValueChange={setReportsTab}>
        <TabsList>
          <TabsTrigger value="campaigns" className="flex items-center">
            <BarChart2 className="h-4 w-4 mr-2" />
            Campaign Reports
          </TabsTrigger>
          <TabsTrigger value="affiliates" className="flex items-center">
            <Users className="h-4 w-4 mr-2" />
            Affiliate Reports
          </TabsTrigger>
          <TabsTrigger value="commissions" className="flex items-center">
            <DollarSign className="h-4 w-4 mr-2" />
            Commission Reports
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="campaigns">
          <Card>
            <CardHeader>
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                  <CardTitle>Campaign Reports</CardTitle>
                  <CardDescription>
                    View and export campaign performance reports across your organization
                  </CardDescription>
                </div>
                <Button 
                  variant="outline" 
                  className="flex items-center gap-2"
                  onClick={handleExportCSV}
                >
                  <DownloadCloud className="h-4 w-4" />
                  Export to CSV
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col md:flex-row gap-4 mb-6">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input 
                    placeholder="Search by campaign name or ID..."
                    className="pl-9"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
                <div className="w-full md:w-48">
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger>
                      <div className="flex items-center gap-2">
                        <Filter className="h-4 w-4" />
                        <SelectValue placeholder="Filter by status" />
                      </div>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Statuses</SelectItem>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="paused">Paused</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                      <SelectItem value="draft">Draft</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Campaign ID</TableHead>
                      <TableHead>Campaign Name</TableHead>
                      <TableHead>Start Date</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Referrals</TableHead>
                      <TableHead className="text-right">Conv. Rate</TableHead>
                      <TableHead className="text-right">Total Rewards</TableHead>
                      <TableHead className="text-center">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {getFilteredReports().length > 0 ? (
                      getFilteredReports().map((report) => (
                        <TableRow key={report.id}>
                          <TableCell className="font-mono text-xs">
                            {report.globalCampaignId}
                          </TableCell>
                          <TableCell className="font-medium">
                            {report.campaignName}
                          </TableCell>
                          <TableCell>
                            {format(new Date(report.startDate), 'MMM d, yyyy')}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className={getStatusBadgeVariant(report.status)}>
                              {report.status.charAt(0).toUpperCase() + report.status.slice(1)}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            {report.successfulReferrals}/{report.totalReferrals}
                          </TableCell>
                          <TableCell className="text-right">
                            {(report.conversionRate * 100).toFixed(1)}%
                          </TableCell>
                          <TableCell className="text-right">
                            ${report.totalRewards.toFixed(2)}
                          </TableCell>
                          <TableCell className="text-center">
                            <Button variant="ghost" size="icon">
                              <BarChart2 className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center py-8 text-gray-500">
                          No campaign reports found. Try adjusting your filters or create a new campaign.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="affiliates">
          <Card>
            <CardHeader>
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                  <CardTitle>Affiliate Program Reports</CardTitle>
                  <CardDescription>
                    Monthly performance reports for your affiliate programs
                  </CardDescription>
                </div>
                <Button 
                  variant="outline" 
                  className="flex items-center gap-2"
                  onClick={handleExportAffiliateCSV}
                >
                  <DownloadCloud className="h-4 w-4" />
                  Export to CSV
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Period</TableHead>
                      <TableHead className="text-right">Active Affiliates</TableHead>
                      <TableHead className="text-right">Total Sales</TableHead>
                      <TableHead className="text-right">Total Commissions</TableHead>
                      <TableHead className="text-right">Conversion Rate</TableHead>
                      <TableHead>Report Date</TableHead>
                      <TableHead className="text-center">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {mockAffiliateReports.map((report) => (
                      <TableRow key={report.id}>
                        <TableCell className="font-medium">
                          {report.period}
                        </TableCell>
                        <TableCell className="text-right">
                          {report.affiliates}
                        </TableCell>
                        <TableCell className="text-right">
                          {report.sales}
                        </TableCell>
                        <TableCell className="text-right">
                          ${report.commissions.toFixed(2)}
                        </TableCell>
                        <TableCell className="text-right">
                          {report.conversionRate.toFixed(1)}%
                        </TableCell>
                        <TableCell>
                          {format(new Date(report.generated), 'MMM d, yyyy')}
                        </TableCell>
                        <TableCell className="text-center">
                          <Button variant="ghost" size="icon">
                            <BarChart2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="commissions">
          <Card>
            <CardHeader>
              <CardTitle>Commission Reports</CardTitle>
              <CardDescription>
                Track and manage affiliate commissions and payments
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-center py-8 text-gray-500">
                Commission reports will be available once your affiliate program has generated sales.
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default CampaignReportsSection;
